import { submitEmail } from '../lib/submitEmail.js';

/*
  Reusable email capture form.
  Usage: createEmailForm({ source: 'novel-01', message: 'Get notified...' })
  Returns a DOM element ready to be mounted anywhere.

  On success, fires window 'email:submitted' with { source } detail,
  so main.js (or anything listening) knows a conversion happened,
  without this file needing to know who's listening.
*/

const SUBSCRIBED_KEY = 'subscribed:myths-shorts-general';

export function isAlreadySubscribed() {
  try {
    return localStorage.getItem(SUBSCRIBED_KEY) === 'true';
  } catch (e) {
    return false;
  }
}

export function createEmailForm({ source, message = 'Get notified.', force = false }) {
  const wrapper = document.createElement('div');
  wrapper.className = 'w-full max-w-sm mx-auto';

  // already subscribed on this device (regardless of which source triggered
  // this form) -> show confirmation, skip the form entirely. `source` is
  // still passed through to Supabase on actual submit, unaffected.
  let alreadySubscribed = false;
  try {
    alreadySubscribed = localStorage.getItem(SUBSCRIBED_KEY) === 'true';
  } catch (e) {
    alreadySubscribed = false;
  }

  if (alreadySubscribed && !force) {
    wrapper.innerHTML = `
      <p style="font-family:var(--font-title);font-style:italic;font-size:14px;color:rgba(244,239,232,0.62);text-align:center;">You're already on the list. \u2713</p>
    `;
    return wrapper;
  }

  wrapper.innerHTML = `
    <form class="ep-form" data-role="email-form">
      <p class="ep-form-copy">${message}</p>
      <input
        type="email"
        name="email"
        placeholder="name@midnight.com"
        required
        class="ep-field"
      />
      <label class="ep-check">
        <input type="checkbox" name="optIn" checked />
        <span class="ep-check-box" aria-hidden="true"></span>
        <span class="ep-check-label">Send me updates on new releases</span>
      </label>
      <button type="submit" class="ep-btn">
        <span data-role="btn-label">Subscribe</span>
      </button>
      <p class="ep-error hidden" data-role="error"></p>
      <p class="ep-toast-line hidden" data-role="success" style="margin-top:10px;">You're on the list.</p>
    </form>
  `;

  const form = wrapper.querySelector('[data-role="email-form"]');
  const errorEl = wrapper.querySelector('[data-role="error"]');
  const successEl = wrapper.querySelector('[data-role="success"]');
  const submitBtn = form.querySelector('button[type="submit"]');

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    errorEl.classList.add('hidden');
    errorEl.classList.remove('ep-error--notice');
    submitBtn.disabled = true;
    const btnLabel = submitBtn.querySelector('[data-role="btn-label"]');
    if (btnLabel) btnLabel.textContent = 'Sending...';

    const email = form.email.value.trim();
    const optIn = form.optIn.checked;

    const result = await submitEmail({ email, source, optIn });

    if (result.ok) {
      form.classList.add('hidden');
      successEl.classList.remove('hidden');
      try {
        localStorage.setItem(SUBSCRIBED_KEY, 'true');
      } catch (e) {
        // subscription still succeeded server-side even if local persistence fails
      }
      window.dispatchEvent(
        new CustomEvent('email:submitted', { detail: { source } })
      );
    } else if (result.alreadyExists) {
      // Email is already on file in Supabase (possibly from a different
      // device, or after localStorage was cleared). Nothing was written -
      // the unique constraint rejected the insert. Show a clear, friendly
      // notice distinct from a real error. Do NOT set SUBSCRIBED_KEY here -
      // that flag means "this device has submitted", which didn't happen.
      errorEl.textContent = "That email's already on the list — you're all set.";
      errorEl.classList.add('ep-error--notice');
      errorEl.classList.remove('hidden');
      submitBtn.disabled = false;
      const label = submitBtn.querySelector('[data-role="btn-label"]');
      if (label) label.textContent = 'Subscribe';
    } else {
      errorEl.textContent = result.error;
      errorEl.classList.remove('hidden');
      submitBtn.disabled = false;
      const label = submitBtn.querySelector('[data-role="btn-label"]');
      if (label) label.textContent = 'Subscribe';
    }
  });

  return wrapper;
}


/*
  showEmailPopover — shared bottom-center popover for "Coming Soon" /
  locked items across the whole site (StoryPicker's ring cards,
  ChapterReader's read-next cards, and anywhere else added later).

  Usage: showEmailPopover({ source, message })

  If not yet subscribed: shows createEmailForm() in a bottom-center card.
  If already subscribed: shows a 5-second auto-dismiss toast
  ("You're already on the list. Tap to update your email →") -
  tapping it re-opens the form (so they can submit a different email).

  Rule 74 compliant: the popover element is built once and cached,
  reused across calls, visibility toggled rather than recreated.
*/

let cachedPopover = null;

function buildPopover() {
  const el = document.createElement('div');
  el.dataset.role = 'email-popover';
  el.className = 'fixed left-1/2 z-[10000] w-[92%] max-w-sm';
  el.style.bottom = '24px';
  el.style.transform = 'translateX(-50%) translateY(12px)';
  el.style.opacity = '0';
  el.style.pointerEvents = 'none';
  el.style.transition = 'opacity 0.35s ease, transform 0.35s cubic-bezier(0.16, 1, 0.3, 1)';

  const card = document.createElement('div');
  card.className = 'ep-glass ep-form-card';
  card.style.position = 'relative';

  const edge = document.createElement('span');
  edge.className = 'ep-glass-edge';
  edge.setAttribute('aria-hidden', 'true');

  const closeBtn = document.createElement('button');
  closeBtn.setAttribute('aria-label', 'Close');
  closeBtn.className = 'ep-x';
  closeBtn.style.position = 'absolute';
  closeBtn.style.top = '14px';
  closeBtn.style.right = '14px';
  closeBtn.textContent = '\u2715';
  closeBtn.addEventListener('click', () => hidePopover());

  const content = document.createElement('div');
  content.dataset.role = 'email-popover-content';
  content.style.paddingRight = '30px';

  card.append(edge, closeBtn, content);
  el.appendChild(card);
  document.body.appendChild(el);

  let autoDismissTimer = null;

  function showPopover({ source, message }) {
    if (autoDismissTimer) {
      clearTimeout(autoDismissTimer);
      autoDismissTimer = null;
    }

    // If something (e.g. ChapterReader's overlay) is currently in real
    // browser fullscreen, only that element's subtree renders on screen -
    // any fixed-position element left in document.body would be invisible
    // or misplaced. Re-parent the popover into the fullscreen element for
    // the duration it's shown; falls back to document.body otherwise.
    const fsEl = document.fullscreenElement || document.webkitFullscreenElement;
    const targetParent = fsEl || document.body;
    if (el.parentElement !== targetParent) {
      targetParent.appendChild(el);
    }

    content.innerHTML = '';

    if (isAlreadySubscribed()) {
      card.classList.remove('ep-form-card');
      card.classList.add('ep-toast-card');
      content.style.paddingRight = '0';

      const toastLine = document.createElement('p');
      toastLine.className = 'ep-toast-line';
      toastLine.textContent = "You're already on the list. ";

      const swap = document.createElement('button');
      swap.type = 'button';
      swap.className = 'ep-toast-swap';
      swap.textContent = 'Tap to update your email \u2192';
      swap.addEventListener('click', () => {
        if (autoDismissTimer) clearTimeout(autoDismissTimer);
        card.classList.remove('ep-toast-card');
        card.classList.add('ep-form-card');
        content.style.paddingRight = '30px';
        content.innerHTML = '';
        content.appendChild(createEmailForm({ source, message, force: true }));
      });

      toastLine.appendChild(swap);
      content.appendChild(toastLine);

      const fuse = document.createElement('div');
      fuse.className = 'ep-fuse';
      const fuseBar = document.createElement('span');
      fuseBar.className = 'ep-fuse-bar';
      fuse.appendChild(fuseBar);
      card.appendChild(fuse);

      autoDismissTimer = setTimeout(() => hidePopover(), 5000);
    } else {
      content.appendChild(createEmailForm({ source, message }));
    }

    el.style.opacity = '1';
    el.style.transform = 'translateX(-50%) translateY(0)';
    el.style.pointerEvents = 'auto';
  }

  function hidePopover() {
    if (autoDismissTimer) {
      clearTimeout(autoDismissTimer);
      autoDismissTimer = null;
    }
    el.style.opacity = '0';
    el.style.transform = 'translateX(-50%) translateY(12px)';
    el.style.pointerEvents = 'none';
  }

  return { showPopover, hidePopover };
}

export function showEmailPopover({ source, message = 'Get notified.' }) {
  if (!cachedPopover) {
    cachedPopover = buildPopover();
  }
  cachedPopover.showPopover({ source, message });
}
